import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { Spin } from "antd";

export default function EquipmentPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tous");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);   // item clicked
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(null); // item just requested
  const [fullName, setFullName] = useState("");

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };
  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const init = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const snap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
          if (!snap.empty) setFullName(snap.docs[0].data().fullname || "");
        } catch {}
      }
      try {
        const snap = await getDocs(collection(db, "equipment"));
        setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    init();
  }, []);

  const types = ["Tous", ...Array.from(new Set(items.map(i => i.type).filter(Boolean)))];

  const filtered = items.filter(item => {
    const matchType = filter === "Tous" || item.type === filter;
    const matchSearch = !search || item.nom?.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const available   = filtered.filter(i => i.statut !== "Non Prêter");
  const unavailable = filtered.filter(i => i.statut === "Non Prêter");

  const handleRequest = async () => {
    if (!selected) return;
    setRequesting(true);
    try {
      await addDoc(collection(db, "borrow"), {
        itemId:    selected.id,
        itemTitle: selected.title,
        itemNom:   selected.nom,
        itemType:  selected.type,
        userId:    auth.currentUser?.uid || null,
        userName:  fullName,
        status:    "pending",
        createdAt: new Date(),
      });
      setRequested(selected);
      setSelected(null);
    } catch (err) {
      console.error(err);
    }
    setRequesting(false);
  };

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Équipements</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Matériel disponible à l'emprunt</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {/* Info banner */}
        <div style={{ margin: "16px 18px 0", background: t.blueBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>ℹ️</span>
          <div style={{ fontSize: 12, color: t.blueText, lineHeight: 1.6 }}>
            Cliquez sur un équipement <strong>disponible</strong> pour faire une demande d'emprunt. Présentez-vous ensuite à <strong>l'accueil</strong> pour récupérer l'article.
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: "14px 18px 0" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Rechercher un équipement..."
            style={{ width: "100%", height: 42, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, padding: "0 14px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Type filter */}
        <div style={{ display: "flex", gap: 8, padding: "12px 18px 0", overflowX: "auto", scrollbarWidth: "none" }}>
          {types.map(type => (
            <button key={type} onClick={() => setFilter(type)} style={{
              padding: "6px 14px", borderRadius: 20,
              border: `1.5px solid ${filter === type ? t.blue : t.border}`,
              background: filter === type ? t.blueBg : t.bg3,
              color: filter === type ? t.blueText : t.text2,
              fontSize: 12, fontWeight: filter === type ? 600 : 400,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}>{type}</button>
          ))}
        </div>

        {/* Success banner */}
        {requested && (
          <div style={{ margin: "16px 18px 0", background: t.greenBg, border: `1px solid ${t.green}33`, borderRadius: 14, padding: "14px 16px", display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.green }}>Demande envoyée !</div>
              <div style={{ fontSize: 12, color: t.text2, marginTop: 2 }}>
                Rendez-vous à l'accueil pour récupérer <strong>{requested.nom}</strong>.
              </div>
            </div>
            <button onClick={() => setRequested(null)} style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 16, color: t.text3, cursor: "pointer" }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}><Spin /></div>
        ) : (
          <div style={{ padding: "16px 18px 0" }}>

            {/* Available */}
            {available.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.green, marginBottom: 10 }}>
                  ✅ Disponible ({available.length})
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10, marginBottom: 20 }}>
                  {available.map(item => (
                    <EquipCard key={item.id} item={item} t={t} available={true} onClick={() => setSelected(item)} />
                  ))}
                </div>
              </>
            )}

            {/* Unavailable */}
            {unavailable.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.text3, marginBottom: 10 }}>
                  ⛔ Non disponible ({unavailable.length})
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10 }}>
                  {unavailable.map(item => (
                    <EquipCard key={item.id} item={item} t={t} available={false} onClick={null} />
                  ))}
                </div>
              </>
            )}

            {filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0", color: t.text3, fontSize: 13 }}>
                Aucun équipement trouvé.
              </div>
            )}

            {/* Reception reminder */}
            
          </div>
        )}
      </div>

      {/* Request Modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 0 24px" }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ width: "100%", maxWidth: 440, borderRadius: "20px 20px 16px 16px", background: t.cardBg, border: `1px solid ${t.border}`, boxShadow: "0 -4px 32px rgba(0,0,0,0.2)", padding: "20px 18px", animation: "slideUp 0.3s ease" }}>

            {/* Item preview */}
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 12, overflow: "hidden", background: t.bg3, flexShrink: 0 }}>
                {selected.image ? (
                  <img src={selected.image} alt={selected.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🎧</div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.text1 }}>{selected.nom}</div>
                <div style={{ fontSize: 12, color: t.text2, marginTop: 2 }}>#{selected.title}</div>
                {selected.type && (
                  <div style={{ marginTop: 4, display: "inline-block", fontSize: 10, padding: "2px 8px", borderRadius: 20, background: t.blueBg, color: t.blueText, fontWeight: 500 }}>
                    {selected.type}
                  </div>
                )}
              </div>
            </div>

            <div style={{ height: 1, background: t.border, marginBottom: 16 }} />

            <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 20 }}>
              Vous souhaitez emprunter cet équipement ? Une fois confirmé, rendez-vous à <strong>l'accueil WeStudy</strong> pour le récupérer.
            </div>

            {fullName && (
              <div style={{ background: t.bg3, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: t.text2, marginBottom: 16 }}>
                👤 Demande au nom de : <strong style={{ color: t.text1 }}>{fullName}</strong>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSelected(null)}
                style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg3, color: t.text2, fontSize: 13, cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={handleRequest} disabled={requesting}
                style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: requesting ? t.bg3 : t.blue, color: requesting ? t.text3 : "#fff", fontSize: 13, fontWeight: 600, cursor: requesting ? "not-allowed" : "pointer" }}>
                {requesting ? "Envoi..." : "✅ Confirmer la demande"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

function EquipCard({ item, t, available, onClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div onClick={available ? onClick : undefined} style={{
      background: t.cardBg, border: `1.5px solid ${t.border}`,
      borderRadius: 14, overflow: "hidden", opacity: available ? 1 : 0.55,
      cursor: available ? "pointer" : "default", position: "relative",
      transition: "border-color 0.15s",
    }}
      onMouseEnter={e => { if (available) e.currentTarget.style.borderColor = t.blue; }}
      onMouseLeave={e => { if (available) e.currentTarget.style.borderColor = t.border; }}
    >
      {/* Image */}
      <div style={{ width: "100%", height: 110, background: t.bg3, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {item.image && !imgError ? (
          <img src={item.image} alt={item.nom} onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontSize: 36 }}>🎧</span>
        )}
      </div>

      {/* Status badge */}
      <div style={{ position: "absolute", top: 8, right: 8, background: available ? t.green : t.red, color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20 }}>
        {available ? "Disponible" : "Emprunté"}
      </div>

      {/* Tap hint */}
      {available && (
        <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 9, padding: "2px 6px", borderRadius: 10 }}>
          Appuyer
        </div>
      )}

      {/* Info */}
      <div style={{ padding: "10px 12px" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 2, lineHeight: 1.3 }}>{item.nom}</div>
        <div style={{ fontSize: 10, color: t.text3 }}>#{item.title}</div>
        {item.type && (
          <div style={{ marginTop: 6, display: "inline-block", fontSize: 10, padding: "2px 8px", borderRadius: 20, background: t.blueBg, color: t.blueText, fontWeight: 500 }}>
            {item.type}
          </div>
        )}
      </div>
    </div>
  );
}

const theme = {
  light: { bg:"#f8fafc",bg3:"#f1f5f9",border:"#e2e8f0",text1:"#0f172a",text2:"#64748b",text3:"#94a3b8",blue:"#2563eb",blueBg:"#eff6ff",blueText:"#1d4ed8",green:"#16a34a",greenBg:"#f0fdf4",amber:"#d97706",amberBg:"#fffbeb",amberText:"#b45309",amberBorder:"#fde68a",red:"#dc2626",cardBg:"#ffffff" },
  dark:  { bg:"#0f172a",bg3:"#1a2740",border:"#273549",text1:"#f1f5f9",text2:"#94a3b8",text3:"#475569",blue:"#378ADD",blueBg:"#1e3a5f",blueText:"#85B7EB",green:"#4ade80",greenBg:"#052e16",amber:"#fbbf24",amberBg:"#2a1e07",amberText:"#fcd34d",amberBorder:"#78350f",red:"#f87171",cardBg:"#1e293b" },
};

const s = {
  toggleTrack: { width:36,height:20,borderRadius:20,cursor:"pointer",position:"relative",flexShrink:0 },
  toggleKnob:  { width:14,height:14,borderRadius:"50%",position:"absolute",top:2,left:2,transition:"transform 0.2s" },
};